import React from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { getActionData } from '../../../utils';

import type { datePickerField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/datePickerField_updateWorkItemAllFieldTypesActionRenderer.graphql';
import { formatSuggestedDate } from './utlis';

const messages = defineMessages({
	set: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.date-picker-field.set',
		defaultMessage: 'Set {name} to {value}',
		description: 'Label for the change action in the date picker field',
	},
	unset: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.date-picker-field.unset',
		defaultMessage: 'Remove date from {name}',
		description: 'Label for the change action in the date picker field',
	},
});

export type Props = {
	queryRef: datePickerField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
};

export const DatePickerField = ({ queryRef, action }: Props): React.JSX.Element | null => {
	const { formatMessage, formatDate } = useIntl();

	const { fieldKey, fieldValue } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment datePickerField_updateWorkItemAllFieldTypesActionRenderer on JiraDatePickerField {
				fieldId
				name
			}
		`,
		queryRef,
	);

	if (!data?.fieldId || data?.fieldId !== fieldKey) {
		return null;
	}

	const fieldName = data?.name ? data.name.toLowerCase() : fieldKey;
	const formattedDate = formatSuggestedDate(fieldValue, formatDate);

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.date-picker-field"
		>
			{formattedDate
				? formatMessage(messages.set, { name: fieldName, value: formattedDate })
				: formatMessage(messages.unset, { name: fieldName })}
		</Text>
	);
};
